import React, { useEffect, useState } from 'react';
import type { CommunityTopic, TopicReply } from './types';
import { formatForumDateFull, isOwnCommunityMessage, memberAuthorFromProfile, topicMetaLine } from './communityUtils';
import { AuthorBadge, TopicSummaryBlock } from './CommunityShared';
import { useCommunityForum } from './CommunityForumContext';
import { CommunityDevAdminGate, CommunityDevEntity, CommunityDevPath } from './CommunityDevUi';
import {
  CommunityMessageAdminActions,
  CommunityMessageContent,
  useCommunityMessageEdit,
} from './CommunityMessageActions';
import { entityRef, P } from './communityDevMode';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  topic: CommunityTopic;
  sectionName: string;
  onBack: () => void;
  realIsAdmin: boolean;
};

type TopicReplyRowProps = {
  reply: TopicReply;
  isOwn: boolean;
  isAdmin: boolean;
  realIsAdmin: boolean;
  onSave: (body: string) => void;
  onDelete: () => void;
};

function TopicReplyRow({ reply, isOwn, isAdmin, realIsAdmin, onSave, onDelete }: TopicReplyRowProps) {
  const edit = useCommunityMessageEdit(reply.body, onSave);

  return (
    <CommunityDevEntity storagePath={entityRef(P.replies, reply.id)}>
      <article className="dcf-reply">
        <div className="dcf-reply__header">
          <AuthorBadge author={reply.author} devPath={P.replyAuthor} />
          <div className="dcf-message-meta">
            <time className="dcf-reply__time" dateTime={reply.updatedAt || reply.createdAt}>
              {formatForumDateFull(reply.updatedAt || reply.createdAt)}
            </time>
            <CommunityMessageAdminActions
              canEdit={isAdmin && isOwn}
              canDelete={isAdmin}
              realIsAdmin={realIsAdmin}
              isAdmin={isAdmin}
              deleteLabel="Удаление ответа"
              storagePath={entityRef(P.replies, reply.id)}
              onEdit={edit.startEdit}
              onDelete={onDelete}
              hidden={edit.editing}
            />
            <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => window.alert('Жалоба принята.')}>
              Пожаловаться
            </button>
          </div>
        </div>
        <CommunityMessageContent
          body={reply.body}
          bodyDevPath={<CommunityDevPath path={P.replyBody} inline />}
          editing={edit.editing}
          draft={edit.draft}
          setDraft={edit.setDraft}
          cancelEdit={edit.cancelEdit}
          saveEdit={edit.saveEdit}
        />
        <CommunityDevPath path={P.replyTopicId} />
      </article>
    </CommunityDevEntity>
  );
}

export default function CommunityTopicDetailView({ topic, sectionName, onBack, realIsAdmin }: Props) {
  const { user } = useAuth();
  const {
    getTopicReplies,
    addReply,
    editReply,
    isAdmin,
    setTopicStatus,
    addSummary,
    removeTopic,
    removeReply,
    loadTopicReplies,
  } = useCommunityForum();
  const replies = getTopicReplies(topic.id);

  useEffect(() => {
    void loadTopicReplies(topic.id);
    const interval = window.setInterval(() => {
      void loadTopicReplies(topic.id);
    }, 5000);
    return () => window.clearInterval(interval);
  }, [topic.id, loadTopicReplies]);

  const [replyBody, setReplyBody] = useState('');
  const [replyAnonymous, setReplyAnonymous] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState(topic.summary?.body || '');
  const [showSummaryForm, setShowSummaryForm] = useState(false);

  const isClosed = topic.status === 'closed';
  const canReply = !isClosed && topic.status !== 'hidden';

  const sendReply = () => {
    if (!replyBody.trim()) return;
    void addReply(topic.id, replyBody, memberAuthorFromProfile(replyAnonymous, user?.email), user?.email);
    setReplyBody('');
  };

  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#/private-circle/community?topic=${topic.id}`;
    void navigator.clipboard?.writeText(url);
  };

  const saveSummary = () => {
    if (!summaryDraft.trim()) return;
    addSummary(topic.id, { body: summaryDraft.trim(), preparedBy: isAdmin ? 'datacircle' : 'moderator' });
    setShowSummaryForm(false);
  };

  const adminActions = (
    <>
      <button type="button" className="dcf-btn dcf-btn--ghost" onClick={() => setTopicStatus(topic.id, topic.status === 'pinned' ? 'open' : 'pinned')}>
        {topic.status === 'pinned' ? 'Открепить' : 'Закрепить'}
      </button>
      <button type="button" className="dcf-btn dcf-btn--ghost" onClick={() => setTopicStatus(topic.id, isClosed ? 'open' : 'closed')}>
        {isClosed ? 'Открыть тему' : 'Закрыть тему'}
      </button>
      <button type="button" className="dcf-btn dcf-btn--ghost" onClick={() => setTopicStatus(topic.id, 'hidden')}>Скрыть</button>
      <button type="button" className="dcf-btn dcf-btn--ghost" onClick={() => setShowSummaryForm((v) => !v)}>
        {topic.hasSummary ? 'Изменить сводку' : 'Добавить сводку'}
      </button>
      <button type="button" className="dcf-btn dcf-btn--ghost dcf-btn--danger" onClick={() => { if (window.confirm('Удалить тему?')) { removeTopic(topic.id); onBack(); } }}>
        Удалить тему
      </button>
    </>
  );

  return (
    <>
      <button type="button" className="dcf-btn dcf-btn--ghost dcf-topic-detail__back" onClick={onBack}>
        ← К темам раздела «{sectionName}»
      </button>

      <CommunityDevEntity storagePath={entityRef(P.topics, topic.id)}>
        <div className="dcf-topic-card__badges mb-3">
          {topic.status === 'pinned' && <span className="dcf-badge dcf-badge--pinned">Закреплено</span>}
          {topic.hasSummary && <span className="dcf-badge dcf-badge--summary">Есть сводка</span>}
          {topic.status === 'closed' && <span className="dcf-badge dcf-badge--closed">Закрыта</span>}
          <CommunityDevPath path={P.topicStatus} inline />
        </div>

        <h1 className="dcf-topic-detail__title">
          {topic.title}
          <CommunityDevPath path={P.topicTitle} inline />
        </h1>

        <div className="flex flex-wrap gap-2 mb-4">
          {topic.tags.map((tag) => (
            <span key={tag} className="dcf-tag">{tag}</span>
          ))}
          <CommunityDevPath path={P.topicTags} inline />
        </div>

        <div className="dcf-topic-detail__meta-row">
          <AuthorBadge author={topic.author} devPath={P.topicAuthor} />
          <span className="text-sm text-slate-400">{topicMetaLine(topic)}</span>
          <span className="text-sm text-slate-400">{formatForumDateFull(topic.createdAt)}</span>
        </div>

        <p className="dcf-topic-detail__desc">
          {topic.description}
          <CommunityDevPath path={P.topicDescription} inline />
        </p>
      </CommunityDevEntity>

      <div className="dcf-actions-row">
        <button type="button" className="dcf-btn dcf-btn--ghost" onClick={copyLink}>Скопировать ссылку</button>
        <button type="button" className="dcf-btn dcf-btn--ghost" onClick={() => window.alert('Жалоба принята. Модератор рассмотрит обращение.')}>
          Пожаловаться
        </button>
        <CommunityDevAdminGate
          realIsAdmin={realIsAdmin}
          isAdmin={isAdmin}
          label="Модерация темы"
          storagePath={P.topicStatus}
        >
          <>{adminActions}</>
        </CommunityDevAdminGate>
      </div>

      {topic.hasSummary && topic.summary && !showSummaryForm && (
        <div>
          <CommunityDevPath path={P.topicSummary} />
          <TopicSummaryBlock body={topic.summary.body} preparedBy={topic.summary.preparedBy} />
        </div>
      )}

      {showSummaryForm && isAdmin && (
        <div className="dcf-compose mb-6">
          <div className="dcf-compose__title">Сводка обсуждения</div>
          <CommunityDevPath path={P.topicSummary} />
          <textarea className="dcf-textarea" rows={4} value={summaryDraft} onChange={(e) => setSummaryDraft(e.target.value)} />
          <div className="dcf-modal__actions mt-3 border-0 pt-0">
            <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setShowSummaryForm(false)}>Отмена</button>
            <button type="button" className="dcf-btn dcf-btn--primary" onClick={saveSummary}>Сохранить сводку</button>
          </div>
        </div>
      )}

      <h2 className="text-base font-semibold text-slate-800 mb-4">
        Ответы ({replies.length})
        <CommunityDevPath path={P.replies} inline />
      </h2>

      {replies.length === 0 ? (
        <p className="text-sm text-slate-500 mb-6">Пока нет ответов. Будьте первым, кто поделится опытом.</p>
      ) : (
        replies.map((reply) => (
          <TopicReplyRow
            key={reply.id}
            reply={reply}
            isOwn={isOwnCommunityMessage(reply.authorKey, user?.email)}
            isAdmin={isAdmin}
            realIsAdmin={realIsAdmin}
            onSave={(body) => editReply(reply.id, body)}
            onDelete={() => removeReply(reply.id)}
          />
        ))
      )}

      {canReply ? (
        <div className="dcf-compose">
          <div className="dcf-compose__title">Ваш ответ</div>
          <CommunityDevPath path={P.replyBody} />
          <textarea
            className="dcf-textarea"
            rows={4}
            placeholder="Поделитесь опытом и рыночными ориентирами…"
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
          />
          <label className="dcf-toggle-row">
            <input type="checkbox" checked={replyAnonymous} onChange={(e) => setReplyAnonymous(e.target.checked)} />
            Ответить анонимно
          </label>
          <div className="mt-4">
            <button type="button" className="dcf-btn dcf-btn--primary" onClick={sendReply} disabled={!replyBody.trim()}>
              Отправить ответ
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-6 text-sm text-slate-500">Тема закрыта для новых ответов.</p>
      )}
    </>
  );
}
